import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";

export default function Hero() {
  const scrollTo = (href) => (e) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden"
    >
      {/* Background grid + halftone */}
      <div className="absolute inset-0 paper-grid pointer-events-none" />
      <div className="absolute inset-0 halftone-bg pointer-events-none" />

      {/* Floating stickers */}
      <motion.div
        className="absolute top-28 right-[6%] w-24 h-24 bg-[#FFD800] brut-border brut-shadow-lg hidden md:flex items-center justify-center font-display font-black uppercase text-xs rotate-12 z-10"
        animate={{ y: [0, -14, 0], rotate: [12, -6, 12] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        data-testid="hero-sticker-1"
      >
        Ship<br />Fast
      </motion.div>

      <motion.div
        className="absolute bottom-12 right-[12%] px-3 py-1 bg-[#FF0055] text-white brut-border brut-shadow-sm font-script text-xl -rotate-6 z-10"
        animate={{ rotate: [-6, 4, -6] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        data-testid="hero-sticker-3"
      >
        hello!
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 brut-border bg-white px-3 py-1.5 brut-shadow-sm mb-8 font-mono text-xs uppercase tracking-widest"
          data-testid="hero-badge"
        >
          <span className="w-2 h-2 bg-[#00CC44] rounded-full animate-pulse" />
          Currently Accepting New Projects
        </motion.div>

        <h1
          className="font-display font-black uppercase leading-[0.88] tracking-tighter text-[15vw] sm:text-[12vw] lg:text-[8.5rem]"
          data-testid="hero-heading"
        >
          <motion.span
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="block"
          >
            Freelance
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="block"
          >
            digital{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-white px-3">builder</span>
              <span className="absolute inset-0 bg-[#FF0055] -rotate-1 z-0" />
            </span>
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.32 }}
            className="block text-stroke"
          >
            for bold ideas.
          </motion.span>
        </h1>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="lg:col-span-6 text-base sm:text-lg font-body text-[#4A4A4A] max-w-xl"
            data-testid="hero-subheading"
          >
            <span className="font-bold text-[#0D0D0D]">IncInk Creative</span> — Helping local businesses
            automate operations, improve online presence, and get more customers
            using AI and modern software &amp; web apps.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="lg:col-span-6 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              onClick={scrollTo("#contact")}
              data-testid="hero-cta-primary"
              className="btn-primary"
            >
              Start a project <ArrowRight size={18} />
            </a>
            <a
              href="#work"
              onClick={scrollTo("#work")}
              data-testid="hero-cta-secondary"
              className="btn-primary btn-yellow"
            >
              See the work <Zap size={18} />
            </a>
          </motion.div>
        </div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-4"
          data-testid="hero-stats"
        >
          {[
            { k: "30+", v: "Projects shipped" },
            { k: "15d", v: "Avg. MVP timeline" },
            { k: "4.8", v: "Avg. client rating" },
          ].map((s, i) => (
            <div
              key={s.v}
              className={`brut-border bg-white p-5 brut-shadow ${
                i % 2 === 0 ? "rotate-[-0.6deg]" : "rotate-[0.6deg]"
              }`}
            >
              <div className="font-display font-black text-3xl sm:text-4xl">{s.k}</div>
              <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A] mt-1">
                {s.v}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
